<template>
  <div class="attendance-admin-page">
    <div class="monthNavWrap">
      <button @click="prevMonth" class="monthNav">◀︎</button>
      <h2>{{ monthName }} {{ year }}</h2>
      <button @click="nextMonth" class="monthNav">▶︎</button>
    </div>
    <div class="downloadRow">
      <button @click="downloadAllPdf" class="submit-button">Download All</button>
      <button @click="downloadCsv" class="submit-button ghost">Download CSV</button>
    </div>

    <table class="attendanceTable">
      <thead>
        <tr>
          <th>User Name</th>
          <th>Status</th>
          <th>Accrued OT</th>
          <th>Carried Over OT</th>
          <th>Claimed OT</th>
          <th>PDF</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>{{ user.first_name }} {{ user.last_name }}</td>
          <td>
            <span v-if="user.manager_approved">Approved</span>
            <span v-else-if="user.status === 'closed'">Awaiting Approval by Manager</span>
            <span v-else>Open</span>
            <button v-if="user.status === 'closed'" @click="reopenMonth(user)" class="btnReopen" :disabled="user.next_month_is_closed">Reopen Month</button>
          </td>
          <td>{{ getOtValue(user.id, "accruedMinutes") }}</td>
          <td>{{ getOtValue(user.id, "carriedMinutes") }}</td>
          <td>{{ getOtValue(user.id, "claimedMinutes") }}</td>
          <td>
            <button
              @click="downloadPdf(user.id)"
              class="submit-button"
              :disabled="!(user.status === 'closed' && user.manager_approved)"
            >
              Download
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="error">{{ error }}</div>
  </div>
</template>

<script>
import api from "@/services/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";

const FISCAL_MONTH_FIELDS = {
  1: "jan",
  2: "feb",
  3: "mar",
  4: "apr",
  5: "may",
  6: "jun",
  7: "jul",
  8: "aug",
  9: "sep",
  10: "oct",
  11: "nov",
  12: "dec",
};

export default {
  name: "AttendancePDFView",
  data() {
    return {
      month: this.getCurrentMonth(),
      users: [],
      loading: false,
      error: "",
      fontBase64: "",
      otSummaries: {},
      monthlyOtBreakdown: {},
    };
  },
  computed: {
    year() {
      return this.month.split("-")[0];
    },
    monthName() {
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const m = Number(this.month.split("-")[1]);
      return monthNames[m - 1];
    },
  },
  methods: {
    getCurrentMonth() {
      const d = this.$now();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    },
    getMonthNumber() {
      return Number(this.month.split("-")[1]);
    },
    getMonthField() {
      return FISCAL_MONTH_FIELDS[this.getMonthNumber()];
    },
    formatHoursFromMinutes(minutes) {
      if (minutes === null || minutes === undefined) return "";
      return (minutes / 60).toFixed(2);
    },
    roundMinutesToQuarterHour(minutes) {
      if (minutes === null || minutes === undefined) return null;
      const value = Number(minutes);
      if (!Number.isFinite(value)) return null;
      return value >= 0
        ? Math.floor(value / 15) * 15
        : Math.ceil(value / 15) * 15;
    },
    escapeCsv(val) {
      if (val === null || val === undefined) return "";
      const str = String(val);
      if (str.includes('"') || str.includes(",") || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    },
    getOtValue(userId, key) {
      const summary = this.otSummaries[userId];
      if (!summary) return "";
      return this.formatHoursFromMinutes(summary[key]);
    },
    async fetchUsers() {
      this.loading = true;
      this.error = "";
      this.otSummaries = {};
      this.monthlyOtBreakdown = {};
      try {
        const res = await api.get("/attendance/users/", {
          params: { month: this.month },
        });
        this.users = res.data.users;
        await Promise.all([this.fetchOtSummaries(), this.fetchMonthlyOtBreakdown()]);
      } catch (err) {
        console.error("Failed to fetch user information.", err);
        this.error = "Failed to fetch user information.";
      } finally {
        this.loading = false;
      }
    },
    async fetchOtSummaries() {
      if (!this.users.length) return;
      const fiscalYear =
        this.getMonthNumber() >= 4 ? Number(this.year) : Number(this.year) - 1;
      const monthField = this.getMonthField();
      const summaries = {};

      await Promise.all(
        this.users.map(async (user) => {
          let accruedMinutes = 0;
          let carriedMinutes = 0;

          try {
            const overtimeResponse = await api.get("/annual-overtimes/", {
              params: { fiscal_year: fiscalYear, user: user.id },
            });
            const annualOvertime = overtimeResponse.data.length
              ? overtimeResponse.data[0]
              : null;
            accruedMinutes = annualOvertime ? annualOvertime[monthField] || 0 : 0;
          } catch (err) {
            console.warn(
              `Failed to fetch annual overtime for user ${user.id}`,
              err
            );
          }

          try {
            const flexitimeResponse = await api.get("/attendance/flexitime/", {
              params: {
                user_id: user.id,
                fiscal_year: fiscalYear,
                month: this.getMonthNumber(),
              },
            });
            carriedMinutes = flexitimeResponse.data.minutes || 0;
          } catch (err) {
            console.warn(`Failed to fetch flexitime for user ${user.id}`, err);
          }

          const claimedMinutes = Math.max(accruedMinutes - carriedMinutes, 0);
          summaries[user.id] = {
            accruedMinutes,
            carriedMinutes,
            claimedMinutes,
          };
        })
      );

      this.otSummaries = summaries;
    },
    async fetchMonthlyOtBreakdown() {
      try {
        const res = await api.get("/attendance/monthly_overtime_summary/", {
          params: { year: this.year, month: this.getMonthNumber() },
        });
        this.monthlyOtBreakdown = res.data.summaries || {};
      } catch (err) {
        console.warn("Failed to fetch monthly overtime breakdown.", err);
        this.monthlyOtBreakdown = {};
      }
    },
    async downloadCsv() {
      // Ensure OT summaries are available
      const needOtSummary = !Object.keys(this.otSummaries).length;
      const needBreakdown = !Object.keys(this.monthlyOtBreakdown).length;
      if (needOtSummary || needBreakdown) {
        await Promise.all([
          needOtSummary ? this.fetchOtSummaries() : Promise.resolve(),
          needBreakdown ? this.fetchMonthlyOtBreakdown() : Promise.resolve(),
        ]);
      }
      const headers = [
        "Name",
        "Accrued OT",
        "Carried Over OT",
        "Claimed OT",
        "Teams",
        "Employment Type",
        "Weekday OT",
        "Weekday Late-Night OT",
        "Weekend/Holiday OT",
        "Weekend/Holiday Late-Night OT",
        "Lunch Allowance (days)",
      ];
      const rows = this.users.map((user) => {
        const name = `${user.first_name} ${user.last_name}`.trim();
        const teams = Array.isArray(user.teams) ? user.teams.join(" / ") : "";
        const canShowDetails =
          user.manager_approved === true && user.status === "closed";

        const summary = canShowDetails ? this.otSummaries[user.id] || {} : {};
        const breakdown =
          canShowDetails && user.employment_type === "Expat"
            ? this.monthlyOtBreakdown[user.id] || {}
            : {};

        const roundedWeekdayOt = this.roundMinutesToQuarterHour(
          breakdown.weekday_ot_minutes
        );
        const roundedWeekdayLateNightOt = this.roundMinutesToQuarterHour(
          breakdown.weekday_late_night_ot_minutes
        );
        const roundedWeekendHolidayOt = this.roundMinutesToQuarterHour(
          breakdown.weekend_holiday_ot_minutes
        );
        const roundedWeekendHolidayLateNightOt = this.roundMinutesToQuarterHour(
          breakdown.weekend_holiday_late_night_ot_minutes
        );

        const lunchAllowance =
          canShowDetails && user.employment_type === "Expat"
            ? breakdown.lunch_allowance_days ?? ""
            : "";
        return [
          name,
          this.formatHoursFromMinutes(summary.accruedMinutes),
          this.formatHoursFromMinutes(summary.carriedMinutes),
          this.formatHoursFromMinutes(summary.claimedMinutes),
          teams,
          user.employment_type || "",
          this.formatHoursFromMinutes(
            roundedWeekdayOt
          ),
          this.formatHoursFromMinutes(
            roundedWeekdayLateNightOt
          ),
          this.formatHoursFromMinutes(
            roundedWeekendHolidayOt
          ),
          this.formatHoursFromMinutes(
            roundedWeekendHolidayLateNightOt
          ),
          lunchAllowance,
        ]
          .map(this.escapeCsv)
          .join(",");
      });
      const csvContent = [headers.join(","), ...rows].join("\n");
      // Prepend BOM for Excel compatibility and ensure all users are included as-is
      const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `attendance-${this.year}-${this.month.split("-")[1]}.csv`;
      link.click();
    },
    prevMonth() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m - 2, 1);
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
    },
    nextMonth() {
      const [y, m] = this.month.split("-").map(Number);
      const d = new Date(y, m, 1);
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
    },
    async ensureFont(doc) {
      if (!this.fontBase64) {
        // Load TTF font once, then reuse for each document
        const res = await fetch("/fonts/NotoSansJP-Regular.ttf");
        const buffer = await res.arrayBuffer();
        const uint8Array = new Uint8Array(buffer);
        let binary = "";
        for (let i = 0; i < uint8Array.length; i++) {
          binary += String.fromCharCode(uint8Array[i]);
        }
        this.fontBase64 = btoa(binary);
      }
      // Fonts are per-document in jsPDF, so register every time
      doc.addFileToVFS("NotoSansJP-Regular.ttf", this.fontBase64);
      doc.addFont("NotoSansJP-Regular.ttf", "NotoSansJP", "normal");
      doc.setFont("NotoSansJP");
    },
    parseDurationToSeconds(value) {
      if (!value) return null;
      const normalized = value.trim();
      const withoutFraction = normalized.replace(/(\d{1,2}:\d{2}:\d{2})\.\d+/, "$1");
      const dayPattern = /^(-?\d+)\s+day[s]?,?\s*(\d{1,2}):(\d{2}):(\d{2})$/;
      const daylessPattern = /^(-?\d+)\s+(\d{1,2}):(\d{2}):(\d{2})$/;
      const timePattern = /^(-?)(\d{1,2}):(\d{2}):(\d{2})$/;

      const dayMatch = withoutFraction.match(dayPattern);
      if (dayMatch) {
        const [, days, h, m, s] = dayMatch;
        return Number(days) * 86400 + Number(h) * 3600 + Number(m) * 60 + Number(s);
      }

      const daylessMatch = withoutFraction.match(daylessPattern);
      if (daylessMatch) {
        const [, days, h, m, s] = daylessMatch;
        return Number(days) * 86400 + Number(h) * 3600 + Number(m) * 60 + Number(s);
      }

      const timeMatch = withoutFraction.match(timePattern);
      if (timeMatch) {
        const [, sign, h, m, s] = timeMatch;
        const baseSeconds = Number(h) * 3600 + Number(m) * 60 + Number(s);
        return (sign === "-" ? -1 : 1) * baseSeconds;
      }
      return null;
    },
    formatDurationHhMm(durationStr) {
      if (!durationStr) return "";
      const totalSeconds = this.parseDurationToSeconds(durationStr);
      if (totalSeconds === null || Number.isNaN(totalSeconds)) {
        return durationStr;
      }
      const sign = totalSeconds < 0 ? "-" : "";
      const absSeconds = Math.abs(totalSeconds);
      const hours = Math.floor(absSeconds / 3600);
      const minutes = Math.floor((absSeconds % 3600) / 60);
      return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
    },
    hasPositiveDuration(durationStr) {
      const seconds = this.parseDurationToSeconds(durationStr);
      return seconds !== null && seconds > 0;
    },
    getOvertimeDisplay(record) {
      // 休日残業がある場合のみそれを表示。ない場合は従来計算（平日含む）に戻す。
      if (record && record.overtime && this.hasPositiveDuration(record.overtime.weekend_holiday_overtime)) {
        return this.formatDurationHhMm(record.overtime.weekend_holiday_overtime);
      }
      if (record && record.overtime) {
        const weekdaySeconds = this.parseDurationToSeconds(record.overtime.weekday_overtime);
        if (weekdaySeconds !== null && weekdaySeconds < 0) {
          return this.formatDurationHhMm(record.overtime.weekday_overtime);
        }
      }
      const fallback = this.calcOvertime(record);
      return fallback === "0:00" ? "" : fallback;
    },
    calcOvertime(rec) {
      if (!rec.clock_in || !rec.clock_out) return "0:00";
      const inMs = new Date(`${rec.date}T${rec.clock_in}`).getTime();
      const outMs = new Date(`${rec.date}T${rec.clock_out}`).getTime();
      const lunchMs = (rec.lunch_minutes || 60) * 60 * 1000;
      const workedMs = outMs - inMs - lunchMs;
      const thresholdMs = 7.5 * 60 * 60 * 1000; // 7.5時間
      if (workedMs <= thresholdMs) return "0:00";
      const overMs = workedMs - thresholdMs;
      const h = Math.floor(overMs / 3600000);
      const m = Math.floor((overMs % 3600000) / 60000);
      const overtimeStr = `${h}:${String(m).padStart(2, "0")}`;
      return overtimeStr === "0:00" ? "" : overtimeStr;
    },
    async reopenMonth(user) {
      if (!window.confirm('Do you want to reopen the month?')) {
        return; // User cancelled
      }
      try {
        await api.post('/attendance/monthly-status/', {
          user_id: user.id,
          year: this.year,
          month: Number(this.month.split("-")[1]),
          is_closed: false,
          manager_approved: false,
        });
        alert('Month reopened successfully!');
        user.status = 'open';
        user.manager_approved = false;
      } catch (e) {
        console.error('API Error:', e);
        this.error = "Error";
        alert('Failed to reopen month.');
      }
    },
    async downloadPdf(userId) {
      console.log("--- Starting downloadPdf ---");
      try {
        // 1. Fetch Attendance Records
        const attendanceResponse = await api.get(`/attendance/`, {
          params: {
            user: userId,
            year: this.year,
            month: this.month.split("-")[1],
          },
        });
        const records = attendanceResponse.data;
        const user = this.users.find((u) => u.id === userId);

        // 2. Fetch Annual Overtime Data
        const fiscalYear = Number(this.month.split("-")[1]) >= 4 ? Number(this.year) : Number(this.year) - 1;
        const overtimeResponse = await api.get("/annual-overtimes/", {
          params: { fiscal_year: fiscalYear, user: userId },
        });
        const annualOvertime = overtimeResponse.data.length ? overtimeResponse.data[0] : null;

        // 3. Fetch Monthly FlexiTime
        let flexitimeMinutes = 0;
        try {
          const flexitimeResponse = await api.get("/attendance/flexitime/", {
            params: {
              user_id: userId,
              fiscal_year: fiscalYear,
              month: this.month.split("-")[1],
            },
          });
          flexitimeMinutes = flexitimeResponse.data.minutes || 0;
        } catch (flexiErr) {
          console.warn("Failed to fetch MonthlyFlexiTime, defaulting to 0:", flexiErr);
          // Continue without flexitime if not found
        }

        // 4. Calculate Summary Data
        const monthField = FISCAL_MONTH_FIELDS[Number(this.month.split("-")[1])];
        const rawOvertimeMinutes = annualOvertime ? annualOvertime[monthField] || 0 : 0;
        let totalOvertimeMinutes = rawOvertimeMinutes;

        // Deduct flexitime from total overtime
        totalOvertimeMinutes -= flexitimeMinutes;
        if (totalOvertimeMinutes < 0) totalOvertimeMinutes = 0; // Ensure overtime doesn't go negative

        const beforeFlexiHours = (rawOvertimeMinutes / 60).toFixed(2);
        const carriedOverHours = (flexitimeMinutes / 60).toFixed(2);
        const totalOvertimeHours = (totalOvertimeMinutes / 60).toFixed(2);
        const daysInOffice = records.filter(r => r.work_location === 'Office').length;

        // 5. Generate PDF
        const doc = new jsPDF();
        await this.ensureFont(doc);
        doc.text(
          `Attendance Record for ${user.first_name} ${user.last_name} - ${this.monthName} ${this.year}`,
          14,
          15
        );

        // Add summary info
        doc.setFontSize(10);
        doc.text(`Accrued OT: ${beforeFlexiHours} hours`, 14, 22);
        doc.text(`Carried Over OT: ${carriedOverHours} hours`, 14, 27);
        doc.text(`Claimed OT: ${totalOvertimeHours} hours`, 14, 32);
        doc.text(`Days in Office: ${daysInOffice} days`, 14, 37);

        const tableColumn = [
          "Date",
          "Clock In",
          "Clock Out",
          "Lunch (min)",
          "Overtime",
          "Reason",
          "Approver",
          "Work Location",
        ];
        const tableRows = [];

        const recordsMap = new Map(
          records.map((record) => [record.date, record])
        );
        const daysInMonth = new Date(
          this.year,
          this.month.split("-")[1],
          0
        ).getDate();

        for (let i = 1; i <= daysInMonth; i++) {
          const date = `${this.year}-${String(
            this.month.split("-")[1]
          ).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
          const record = recordsMap.get(date) || {};

          const rowData = [
            date,
            record.clock_in ? record.clock_in.slice(0, 5) : "",
            record.clock_out ? record.clock_out.slice(0, 5) : "",
            record.lunch_minutes !== undefined ? record.lunch_minutes : "",
            this.getOvertimeDisplay(record),
            record.ot_reason || "",
            record.ot_approver || "",
            record.work_location || "",
          ];
          tableRows.push(rowData);
        }

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: 42, // Adjusted for summary info
          styles: { font: "NotoSansJP" },
          headStyles: { font: "NotoSansJP" },
          bodyStyles: { font: "NotoSansJP" },
          // Fix widths for all columns except Reason (index 5)
          columnStyles: {
            0: { cellWidth: 22 }, // Date
            1: { cellWidth: 18 }, // Clock In
            2: { cellWidth: 20 }, // Clock Out
            3: { cellWidth: 14 }, // Lunch (min)
            4: { cellWidth: 18 }, // Overtime
            6: { cellWidth: 22 }, // Approver
            7: { cellWidth: 20 }, // Work Location
          },
        });

        doc.save(
          `attendance-${user.first_name}_${user.last_name}-${this.year}-${
            this.month.split("-")[1]
          }.pdf`
        );
        console.log("--- PDF download attempt finished ---");
      } catch (error) {
        console.error("Failed to download PDF:", error);
        this.error = "Failed to download PDF.";
      }
    },
    async downloadAllPdf() {
      const zip = new JSZip();
      const pdfsFolder = zip.folder(
        `attendance_pdfs_${this.year}-${this.month.split("-")[1]}`
      );

      for (const user of this.users) {
        // 月締めが完了しているユーザーのみを対象とする
        if (user.status !== "closed") {
          console.log(
            `Skipping PDF download for ${user.first_name} ${user.last_name} as month is not closed.`
          );
          continue;
        }
        try {
          // 1. Fetch Attendance Records
          const attendanceResponse = await api.get(`/attendance/`, {
            params: {
              user: user.id,
              year: this.year,
              month: this.month.split("-")[1],
            },
          });
          const records = attendanceResponse.data;

          // 2. Fetch Annual Overtime Data
          const fiscalYear = Number(this.month.split("-")[1]) >= 4 ? Number(this.year) : Number(this.year) - 1;
          const overtimeResponse = await api.get("/annual-overtimes/", {
            params: { fiscal_year: fiscalYear, user: user.id },
          });
          const annualOvertime = overtimeResponse.data.length ? overtimeResponse.data[0] : null;

          // 3. Fetch Monthly FlexiTime
          let flexitimeMinutes = 0;
          try {
            const flexitimeResponse = await api.get("/attendance/flexitime/", {
              params: {
                user_id: user.id,
                fiscal_year: fiscalYear,
                month: this.month.split("-")[1],
              },
            });
            flexitimeMinutes = flexitimeResponse.data.minutes || 0;
          } catch (flexiErr) {
            console.warn(`Failed to fetch MonthlyFlexiTime for user ${user.id}, defaulting to 0:`, flexiErr);
            // Continue without flexitime if not found
          }

          // 4. Calculate Summary Data
          const monthField = FISCAL_MONTH_FIELDS[Number(this.month.split("-")[1])];
          const rawOvertimeMinutes = annualOvertime ? annualOvertime[monthField] || 0 : 0;
          let totalOvertimeMinutes = rawOvertimeMinutes;

          // Deduct flexitime from total overtime
          totalOvertimeMinutes -= flexitimeMinutes;
          if (totalOvertimeMinutes < 0) totalOvertimeMinutes = 0; // Ensure overtime doesn't go negative

          const beforeFlexiHours = (rawOvertimeMinutes / 60).toFixed(2);
          const carriedOverHours = (flexitimeMinutes / 60).toFixed(2);
          const totalOvertimeHours = (totalOvertimeMinutes / 60).toFixed(2);
          const daysInOffice = records.filter(r => r.work_location === 'Office').length;

          // 5. Generate PDF
          const doc = new jsPDF();
          await this.ensureFont(doc);
          doc.text(
            `Attendance Record for ${user.first_name} ${user.last_name} - ${this.monthName} ${this.year}`,
            14,
            15
          );

          // Add summary info
          doc.setFontSize(10);
          doc.text(`Accrued OT: ${beforeFlexiHours} hours`, 14, 22);
          doc.text(`Carried Over OT: ${carriedOverHours} hours`, 14, 27);
          doc.text(`Claimed OT: ${totalOvertimeHours} hours`, 14, 32);
          doc.text(`Days in Office: ${daysInOffice} days`, 14, 37);

          const tableColumn = [
            "Date",
            "Clock In",
            "Clock Out",
            "Lunch (min)",
            "Overtime",
            "Reason",
            "Approver",
            "Work Location",
          ];
          const tableRows = [];

          const recordsMap = new Map(
            records.map((record) => [record.date, record])
          );
          const daysInMonth = new Date(
            this.year,
            this.month.split("-")[1],
            0
          ).getDate();

          for (let i = 1; i <= daysInMonth; i++) {
            const date = `${this.year}-${String(
              this.month.split("-")[1]
            ).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
            const record = recordsMap.get(date) || {};

            const rowData = [
              date,
              record.clock_in ? record.clock_in.slice(0, 5) : "",
              record.clock_out ? record.clock_out.slice(0, 5) : "",
              record.lunch_minutes !== undefined ? record.lunch_minutes : "",
              this.getOvertimeDisplay(record),
              record.ot_reason || "",
              record.ot_approver || "",
              record.work_location || "",
            ];
            tableRows.push(rowData);
          }

          autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 42, // Adjusted for summary info
            styles: { font: "NotoSansJP" },
            headStyles: { font: "NotoSansJP" },
            bodyStyles: { font: "NotoSansJP" },
            // Fix widths for all columns except Reason (index 5)
            columnStyles: {
              0: { cellWidth: 22 }, // Date
              1: { cellWidth: 18 }, // Clock In
              2: { cellWidth: 20 }, // Clock Out
              3: { cellWidth: 14 }, // Lunch (min)
              4: { cellWidth: 18 }, // Overtime
              6: { cellWidth: 22 }, // Approver
              7: { cellWidth: 20 }, // Work Location
            },
          });

          const pdfBlob = doc.output("blob");
          pdfsFolder.file(
            `attendance-${user.first_name}_${user.last_name}-${this.year}-${
              this.month.split("-")[1]
            }.pdf`,
            pdfBlob
          );
        } catch (error) {
          console.error(
            `Failed to fetch attendance for ${user.first_name} ${user.last_name}:`,
            error
          );
        }
      }

      zip
        .generateAsync({ type: "blob" })
        .then(
          function (content) {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(content);
            link.download = `attendance-all-${this.year}-${
              this.month.split("-")[1]
            }.zip`;
            link.click();
          }.bind(this)
        )
        .catch((error) => {
          console.error("Failed to generate zip file:", error);
          this.error = "Failed to generate ZIP file.";
        });
    },
  },
  watch: {
    month() {
      this.fetchUsers();
    },
  },
  mounted() {
    this.fetchUsers();
  },
};
</script>

<style scoped>
.monthNavWrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}
.monthNav {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0 1rem;
}
.downloadAll { margin-bottom: 1rem;}
.downloadRow {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}
.downloadRow .submit-button {
  width: auto;
  min-width: 150px;
}
.submit-button.ghost {
  background: white;
  color: var(--blue);
  border: 1px solid var(--blue);
}
.attendanceTable {
  width: 100%;
  border-collapse: collapse;
}
.attendanceTable th, .attendanceTable td {
  border: 1px dashed #0757a280;
  padding: 8px;
  text-align:center;
  &:first-child {
    border-left: none;
  }
  &:last-child {
    border-right: none;
  }
}

.attendanceTable th {
  background-color: var(--blue);
  color: white;
  padding: 8px;
  text-align: center;
  font-size: 13px;
  height: 2rem;
  min-width: 50px;
  border: none;
}
.attendanceTable th:first-child { border-top-left-radius: 10px; }
.attendanceTable th:last-child { border-top-right-radius: 10px; }
tr:first-child>td{ border-top:none; }
.download { cursor: pointer; }
.error { color: red; }
.btnReopen {
    border: 1px solid gray;
    padding: 0.5rem;
    border-radius: 5px;
    margin-left: 1rem;
    color: #474747;
}
.submit-button {
  background: var(--grad90);
  width: 100%;
  max-width: 300px;
  padding: 1rem;
  border: none;
  color: white;
  border-radius: 10px;
  margin: 20px auto;
  display: block;
}
.submit-button:disabled {
  background: #cccccc;
  cursor: inherit;
}
button.btnReopen:disabled {
    background: #bfbfbf;
    border: none;
    pointer-events: none;
}
</style>
